import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Map, { Source, Layer, Popup, NavigationControl, FullscreenControl, MapRef } from 'react-map-gl/mapbox';
import type { MapMouseEvent } from 'react-map-gl/mapbox';
import type { FeatureCollection, Feature, Geometry, Point } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import './mapbox-overrides.css';
// Mapbox configuration imported from gitignored private config
import { MAPBOX_TOKEN, MAPBOX_STYLE } from '@/config/private/mapboxConfig';
import type { Business } from '@/pages/Admin/businesses/api/useBusinesses';

// US States GeoJSON
const US_STATES_GEOJSON = 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';

// State name to abbreviation mapping
const stateAbbreviations: Record<string, string> = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
  'District of Columbia': 'DC'
};

// Status colors
const statusColors: Record<string, string> = {
  headquarters: '#a855f7',
  active: '#22c55e',
  expanding: '#f59e0b',
  planned: '#8b5cf6'
};

const statusGlow: Record<string, string> = {
  headquarters: 'rgba(168, 85, 247, 0.6)',
  active: 'rgba(34, 197, 94, 0.5)',
  expanding: 'rgba(245, 158, 11, 0.5)',
  planned: 'rgba(139, 92, 246, 0.4)'
};

// Business pin colors
const BUSINESS_PIN_COLORS = {
  verified: '#22c55e',    // green — verified
  unverified: '#ef4444',  // red — not verified
};

export interface StateInfo {
  status?: string;
  description?: string;
  launchTimeframe?: string;
  schools?: string[];
  cities?: string[];
}

interface USMapProps {
  highlightedStates?: Record<string, StateInfo>;
  onStateClick?: (abbr: string, stateInfo: StateInfo | undefined) => void;
  onStateHover?: (abbr: string | null, stateInfo: StateInfo | undefined | null) => void;
  showTooltip?: boolean;
  businesses?: Business[];
  showStateOverlay?: boolean;
}

interface StatePopupInfo extends StateInfo {
  type: 'state';
  longitude: number;
  latitude: number;
  abbr: string;
  name: string;
}

interface BusinessPopupInfo {
  type: 'business';
  longitude: number;
  latitude: number;
  business: Business;
}

type PopupInfo = StatePopupInfo | BusinessPopupInfo;

interface ViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

function getBusinessPinColor(b: Business): string {
  return b.verified ? BUSINESS_PIN_COLORS.verified : BUSINESS_PIN_COLORS.unverified;
}

const USMap: React.FC<USMapProps> = ({
  highlightedStates = {},
  onStateClick,
  onStateHover,
  showTooltip = true,
  businesses,
  showStateOverlay = true,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = useState<ViewState>({
    longitude: -98.5795,
    latitude: 39.8283,
    zoom: 3.8,
    pitch: 0,
    bearing: 0
  });
  const hoveredFeatureIdRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);
  const [geoData, setGeoData] = useState<FeatureCollection | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  // Build business GeoJSON FeatureCollection
  const businessGeoJson = useMemo<FeatureCollection<Point>>(() => {
    if (!businesses || businesses.length === 0) {
      return { type: 'FeatureCollection', features: [] };
    }
    const features: Feature<Point>[] = businesses
      .filter(b => b.location_lat != null && b.location_lng != null)
      .map(b => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [b.location_lng!, b.location_lat!],
        },
        properties: {
          id: b.id,
          name: b.name,
          address: b.address || '',
          verified: b.verified,
          businessType: b.business_type || '',
          capacity: b.capacity ?? '',
          pinColor: getBusinessPinColor(b),
        },
      }));
    return { type: 'FeatureCollection', features };
  }, [businesses]);

  // Load GeoJSON data
  useEffect(() => {
    fetch(US_STATES_GEOJSON)
      .then(res => res.json())
      .then((data: FeatureCollection) => {
        const features = data.features.map((feature: Feature<Geometry>) => {
          const name = (feature.properties as { name: string })?.name || '';
          const abbr = stateAbbreviations[name] || name;
          const stateInfo = highlightedStates[abbr] || {};
          return {
            ...feature,
            properties: {
              ...feature.properties,
              abbr,
              isHighlighted: !!highlightedStates[abbr],
              status: stateInfo.status || 'none',
              color: stateInfo.status ? statusColors[stateInfo.status] : 'transparent'
            }
          };
        });
        setGeoData({ ...data, features });
      })
      .catch(err => console.error('Failed to load states:', err));
  }, [highlightedStates]);

  const handleMouseMove = useCallback((event: MapMouseEvent) => {
    const features = event.features;
    const map = mapRef.current?.getMap();
    if (!features || features.length === 0) return;

    const feature = features[0];
    const layerId = feature.layer?.id;

    // Ignore business layers for hover (they use click)
    if (layerId === 'business-pins' || layerId === 'business-clusters' || layerId === 'cluster-count') {
      return;
    }

    const featureId = feature.id as number;

    // Update Mapbox feature state synchronously (GPU-side, no React re-render)
    if (map) {
      if (hoveredFeatureIdRef.current !== null && hoveredFeatureIdRef.current !== featureId) {
        map.removeFeatureState({ source: 'states', id: hoveredFeatureIdRef.current });
      }
      map.setFeatureState({ source: 'states', id: featureId }, { hover: true });
      hoveredFeatureIdRef.current = featureId;
    }

    if (onStateHover) {
      const abbr = feature.properties?.abbr as string;
      onStateHover(abbr, highlightedStates[abbr]);
    }

    // Throttle popup updates via rAF to avoid setState on every pixel
    if (showTooltip) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const lng = event.lngLat.lng;
      const lat = event.lngLat.lat;
      const abbr = feature.properties?.abbr as string;
      const name = feature.properties?.name as string;
      rafRef.current = requestAnimationFrame(() => {
        setPopupInfo({
          type: 'state',
          longitude: lng,
          latitude: lat,
          abbr,
          name,
          ...highlightedStates[abbr],
        });
      });
    }
  }, [highlightedStates, onStateHover, showTooltip]);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const map = mapRef.current?.getMap();
    if (map && hoveredFeatureIdRef.current !== null) {
      map.removeFeatureState({ source: 'states', id: hoveredFeatureIdRef.current });
      hoveredFeatureIdRef.current = null;
    }
    // Only clear state popups on mouse leave, keep business popups (they use close button)
    setPopupInfo(prev => prev?.type === 'business' ? prev : null);
    if (onStateHover) {
      onStateHover(null, null);
    }
  }, [onStateHover]);

  const handleClick = useCallback((event: MapMouseEvent) => {
    const features = event.features;
    if (!features || features.length === 0) return;

    const feature = features[0];
    const layerId = feature.layer?.id;

    // Click on cluster -> zoom in
    if (layerId === 'business-clusters') {
      const clusterId = feature.properties?.cluster_id;
      const map = mapRef.current?.getMap();
      if (map && clusterId != null) {
        const source = map.getSource('businesses') as mapboxgl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err || zoom == null) return;
          const geom = feature.geometry as Point;
          mapRef.current?.easeTo({
            center: [geom.coordinates[0], geom.coordinates[1]],
            zoom: zoom,
            duration: 500,
          });
        });
      }
      return;
    }

    // Click on individual business pin -> show popup
    if (layerId === 'business-pins') {
      const geom = feature.geometry as Point;
      const props = feature.properties!;
      // Reconstruct Business-like object from flattened properties
      const biz: Business = {
        id: props.id,
        name: props.name,
        address: props.address || null,
        verified: props.verified === true || props.verified === 'true',
        business_type: props.businessType || null,
        capacity: props.capacity !== '' ? Number(props.capacity) : null,
        email: null,
        phone_number: null,
        location_lat: geom.coordinates[1],
        location_lng: geom.coordinates[0],
        timezone: null,
        business_logo: null,
        created_at: '',
        updated_at: '',
      };
      setPopupInfo({
        type: 'business',
        longitude: geom.coordinates[0],
        latitude: geom.coordinates[1],
        business: biz,
      });
      return;
    }

    // Click on state
    if (onStateClick) {
      const abbr = feature.properties?.abbr as string;
      onStateClick(abbr, highlightedStates[abbr]);
    }
  }, [highlightedStates, onStateClick]);

  // Interactive layer IDs
  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    if (showStateOverlay) ids.push('states-fill');
    if (businesses && businesses.length > 0) {
      ids.push('business-clusters', 'business-pins');
    }
    return ids;
  }, [showStateOverlay, businesses]);

  // Layer for all states fill
  const statesFillLayer = useMemo(() => ({
    id: 'states-fill',
    type: 'fill' as const,
    source: 'states',
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', 'status'], 'headquarters'], statusColors.headquarters,
        ['==', ['get', 'status'], 'active'], statusColors.active,
        ['==', ['get', 'status'], 'expanding'], statusColors.expanding,
        ['==', ['get', 'status'], 'planned'], statusColors.planned,
        'rgba(100, 116, 139, 0.15)'
      ],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false], 0.9,
        ['boolean', ['get', 'isHighlighted'], false], 0.7,
        0.3
      ]
    }
  }), []);

  // Layer for state borders
  const statesLineLayer = useMemo(() => ({
    id: 'states-line',
    type: 'line' as const,
    source: 'states',
    paint: {
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false], '#fff',
        ['boolean', ['get', 'isHighlighted'], false], 'rgba(255, 255, 255, 0.8)',
        'rgba(148, 163, 184, 0.4)'
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false], 3,
        ['boolean', ['get', 'isHighlighted'], false], 2,
        0.5
      ]
    }
  }), []);

  // Glow effect for highlighted states
  const glowLayer = {
    id: 'states-glow',
    type: 'line' as const,
    source: 'states',
    filter: ['boolean', ['get', 'isHighlighted'], false],
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'status'], 'headquarters'], statusGlow.headquarters,
        ['==', ['get', 'status'], 'active'], statusGlow.active,
        ['==', ['get', 'status'], 'expanding'], statusGlow.expanding,
        ['==', ['get', 'status'], 'planned'], statusGlow.planned,
        'transparent'
      ],
      'line-width': 8,
      'line-blur': 6
    }
  };

  // Business cluster circle layer
  const clusterLayer = {
    id: 'business-clusters',
    type: 'circle' as const,
    source: 'businesses',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        'rgba(139, 92, 246, 0.8)',   // < 10: violet
        10, 'rgba(168, 85, 247, 0.8)', // 10-29: purple
        30, 'rgba(124, 58, 237, 0.85)', // 30+: deep purple
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        18,   // < 10
        10, 24, // 10-29
        30, 32, // 30+
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': 'rgba(255, 255, 255, 0.3)',
    },
  };

  // Cluster count label layer
  const clusterCountLayer = {
    id: 'cluster-count',
    type: 'symbol' as const,
    source: 'businesses',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 13,
    },
    paint: {
      'text-color': '#ffffff',
    },
  };

  // Individual business pin layer
  const businessPinLayer = {
    id: 'business-pins',
    type: 'circle' as const,
    source: 'businesses',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['get', 'pinColor'],
      'circle-radius': 7,
      'circle-stroke-width': 2,
      'circle-stroke-color': 'rgba(255, 255, 255, 0.6)',
    },
  };

  return (
    <div className="relative w-full max-w-[1400px] mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-[#0f0f1a] via-[#1a1a2e] to-[#16213e] shadow-[0_0_0_1px_rgba(168,85,247,0.1),0_25px_80px_-20px_rgba(139,92,246,0.4),0_0_100px_-50px_rgba(168,85,247,0.3)]">
      <div className="absolute top-1/2 left-1/2 w-4/5 h-4/5 -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.15)_0%,transparent_70%)] pointer-events-none z-0"></div>

      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        onLoad={() => setIsLoaded(true)}
        style={{ width: '100%', height: '600px', borderRadius: '24px' }}
        mapStyle={MAPBOX_STYLE}
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={interactiveLayerIds}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        attributionControl={false}
        maxBounds={[[-180, 10], [-50, 75]]}
      >
        <NavigationControl position="top-right" showCompass={false} />
        <FullscreenControl position="top-right" />

        {isLoaded && geoData && showStateOverlay && (
          <Source id="states" type="geojson" data={geoData} generateId={true}>
            <Layer {...glowLayer as any} />
            <Layer {...statesFillLayer as any} />
            <Layer {...statesLineLayer as any} />
          </Source>
        )}

        {isLoaded && businessGeoJson.features.length > 0 && (
          <Source
            id="businesses"
            type="geojson"
            data={businessGeoJson}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            <Layer {...clusterLayer as any} />
            <Layer {...clusterCountLayer as any} />
            <Layer {...businessPinLayer as any} />
          </Source>
        )}

        {/* State hover popup */}
        {popupInfo && popupInfo.type === 'state' && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offset={15}
            style={{ zIndex: 100 }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(15, 15, 26, 0.98) 0%, rgba(30, 30, 50, 0.98) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              minWidth: '260px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(139, 92, 246, 0.2)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <span style={{ fontSize: '20px', fontWeight: 'bold', color: 'white', letterSpacing: '-0.02em' }}>{popupInfo.name}</span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'rgba(167, 139, 250, 0.8)', background: 'rgba(139, 92, 246, 0.15)', padding: '4px 10px', borderRadius: '6px' }}>{popupInfo.abbr}</span>
              </div>

              {(popupInfo.status || popupInfo.launchTimeframe || (popupInfo.schools && popupInfo.schools.length) || (popupInfo.cities && popupInfo.cities.length) || popupInfo.description) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {popupInfo.status && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      borderRadius: '9999px',
                      fontSize: '14px',
                      fontWeight: '600',
                      textTransform: 'capitalize',
                      width: 'fit-content',
                      background: popupInfo.status === 'active' ? 'rgba(34, 197, 94, 0.15)' :
                                  popupInfo.status === 'headquarters' ? 'rgba(139, 92, 246, 0.15)' :
                                  popupInfo.status === 'expanding' ? 'rgba(245, 158, 11, 0.15)' :
                                  'rgba(139, 92, 246, 0.15)',
                      color: popupInfo.status === 'active' ? 'rgb(74, 222, 128)' :
                             popupInfo.status === 'headquarters' ? 'rgb(196, 181, 253)' :
                             popupInfo.status === 'expanding' ? 'rgb(252, 211, 77)' :
                             'rgb(167, 139, 250)'
                    }}>
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: popupInfo.status === 'active' ? 'rgb(34, 197, 94)' :
                                   popupInfo.status === 'headquarters' ? 'rgb(168, 85, 247)' :
                                   popupInfo.status === 'expanding' ? 'rgb(245, 158, 11)' :
                                   'rgb(139, 92, 246)'
                      }}></span>
                      {popupInfo.status}
                    </div>
                  )}

                  {popupInfo.launchTimeframe && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                      <svg style={{ width: '16px', height: '16px', color: 'rgba(139, 92, 246, 0.7)', flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <path d="M16 2v4" />
                        <path d="M8 2v4" />
                      </svg>
                      <span><strong>Launch:</strong> {popupInfo.launchTimeframe}</span>
                    </div>
                  )}

                  {popupInfo.schools && popupInfo.schools.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                      <svg style={{ width: '16px', height: '16px', color: 'rgba(139, 92, 246, 0.7)', flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l7 4-7 4-7-4 7-4z" />
                        <path d="M5 10v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
                      </svg>
                      <span><strong>Schools:</strong> {popupInfo.schools.join(', ')}</span>
                    </div>
                  )}

                  {popupInfo.cities && popupInfo.cities.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px' }}>
                      <svg style={{ width: '16px', height: '16px', color: 'rgba(139, 92, 246, 0.7)', flexShrink: 0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      <span><strong>Cities:</strong> {popupInfo.cities.join(', ')}</span>
                    </div>
                  )}

                  {popupInfo.description && (
                    <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px', lineHeight: '1.5', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid rgba(139, 92, 246, 0.1)' }}>{popupInfo.description}</div>
                  )}
                </div>
              ) : (
                <div style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '14px', fontStyle: 'italic' }}>
                  <span>No active presence</span>
                </div>
              )}
            </div>
          </Popup>
        )}

        {/* Business click popup */}
        {popupInfo && popupInfo.type === 'business' && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            closeButton={true}
            closeOnClick={false}
            onClose={() => setPopupInfo(null)}
            anchor="bottom"
            offset={15}
            style={{ zIndex: 100 }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(15, 15, 26, 0.98) 0%, rgba(30, 30, 50, 0.98) 100%)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              minWidth: '260px',
              maxWidth: '320px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(139, 92, 246, 0.2)'
            }}>
              <div style={{ marginBottom: '14px', paddingBottom: '10px', borderBottom: '1px solid rgba(139, 92, 246, 0.2)' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', letterSpacing: '-0.02em', marginBottom: '4px' }}>
                  {popupInfo.business.name}
                </div>
                {popupInfo.business.address && (
                  <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{popupInfo.business.address}</div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {popupInfo.business.business_type && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    <span style={{ color: 'rgba(139, 92, 246, 0.7)', fontWeight: 600, minWidth: '70px' }}>Type</span>
                    <span style={{ textTransform: 'capitalize' }}>{popupInfo.business.business_type}</span>
                  </div>
                )}
                {popupInfo.business.capacity != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                    <span style={{ color: 'rgba(139, 92, 246, 0.7)', fontWeight: 600, minWidth: '70px' }}>Capacity</span>
                    <span>{popupInfo.business.capacity}</span>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                  <span style={{ color: 'rgba(139, 92, 246, 0.7)', fontWeight: 600, minWidth: '70px' }}>Verified</span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '9999px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: popupInfo.business.verified ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: popupInfo.business.verified ? 'rgb(74, 222, 128)' : 'rgb(252, 129, 129)',
                  }}>
                    {popupInfo.business.verified ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </Popup>
        )}
      </Map>

      {/* Loading overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-[#0f0f1a]/[0.98] to-[#1a1a2e]/[0.98] z-[100] gap-6 text-white/70 text-base tracking-wider">
          <div className="relative w-[60px] h-[60px]">
            <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-violet-500 animate-spin"></div>
            <div className="absolute inset-[6px] rounded-full border-[3px] border-transparent border-r-violet-400 animate-spin" style={{ animationDirection: 'reverse', animationDelay: '0.15s' }}></div>
            <div className="absolute inset-[12px] rounded-full border-[3px] border-transparent border-b-violet-600 animate-spin" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <span>Loading map...</span>
        </div>
      )}

      {/* Legend - only active, planned, pilot */}
      <div className="absolute bottom-5 left-5 bg-gradient-to-br from-[#0f0f1a]/90 to-[#1e1e32]/90 backdrop-blur-xl border border-violet-500/20 rounded-2xl p-4 z-10 shadow-[0_10px_40px_rgba(0,0,0,0.3),0_0_30px_rgba(139,92,246,0.1)]">
        {showStateOverlay && (
          <>
            <div className="text-xs font-semibold uppercase tracking-widest text-violet-400/80 mb-3">States</div>
            <div className="flex flex-col gap-2 mb-3">
              <div className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-3.5 h-3.5 rounded bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]"></div>
                <span>Active</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-3.5 h-3.5 rounded bg-violet-400 shadow-[0_0_10px_rgba(139,92,246,0.4)]"></div>
                <span>Planned</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-3.5 h-3.5 rounded bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.4)]"></div>
                <span>Pilot</span>
              </div>
            </div>
          </>
        )}
        {businesses && businesses.length > 0 && (
          <>
            <div className="text-xs font-semibold uppercase tracking-widest text-violet-400/80 mb-3">Business Pins</div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-3.5 h-3.5 rounded-full" style={{ background: BUSINESS_PIN_COLORS.verified, boxShadow: '0 0 10px rgba(34,197,94,0.4)' }}></div>
                <span>Verified</span>
              </div>
              <div className="flex items-center gap-2.5 text-sm text-white/80">
                <div className="w-3.5 h-3.5 rounded-full" style={{ background: BUSINESS_PIN_COLORS.unverified, boxShadow: '0 0 10px rgba(239,68,68,0.4)' }}></div>
                <span>Unverified</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default USMap;
